import {
  EsaMembershipCreditBalancesQuery,
  EsaMembershipCreditBalancesQueryVariables,
} from "@outschool/gql-frontend-generated";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import React from "react";

export const esaMembershipCreditBalanceQuery = gql`
  query EsaMembershipCreditBalances {
    esaMembershipCreditBalances {
      ...EsaMembershipCreditBalancesFragment
    }
  }

  fragment EsaMembershipCreditBalancesFragment on EsaMembershipCreditBalance {
    uid
    availableCredits
    learner {
      uid
      name
      age
    }
    fundingProgram {
      abbreviation
      name
      isClassWallet
    }
  }
`;

export type EsaCreditBalance = {
  availableCredits: number;
  age: number;
  name: string;
  learnerUid: string;
  programName: string;
  programAbbreviation: string;
  isClassWallet: boolean;
  uid: string;
};

export const useEsaMembershipCreditBalances = () => {
  const { isLoggedIn } = useSession();

  const { data, loading, refetch } = useQueryWithPreviousData<
    EsaMembershipCreditBalancesQuery,
    EsaMembershipCreditBalancesQueryVariables
  >(esaMembershipCreditBalanceQuery, {
    skip: !isLoggedIn,
  });

  const creditBalances = React.useMemo(() => {
    const creditBalances = data?.esaMembershipCreditBalances ?? [];
    return creditBalances.map<EsaCreditBalance>(creditBalance => ({
      availableCredits: creditBalance.availableCredits!,
      name: creditBalance.learner.name!,
      age: creditBalance.learner.age!,
      learnerUid: creditBalance.learner.uid!,
      programName: creditBalance.fundingProgram.name!,
      programAbbreviation: creditBalance.fundingProgram.abbreviation!,
      isClassWallet: creditBalance.fundingProgram.isClassWallet!,
      uid: creditBalance.uid,
    }));
  }, [data]);

  return {
    hasEsaMembershipCreditBalances: creditBalances.length > 0,
    creditBalances,
    loading,
    refetch,
  };
};

type EsaMembershipCredit = ReturnType<typeof useEsaMembershipCreditBalances>;

export type EsaMembershipCreditBalance =
  EsaMembershipCredit["creditBalances"][number];
